'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import AutoScroll from 'embla-carousel-auto-scroll';

import { GroqImage } from '@/helpers/Image';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/shadcn/ui/carousel';
import { cn } from '@/lib/utils';
import type { PagePatrickAffiliateSchema } from '@/sanity/pages/PagePatrickAffiliateTemplate';
import { measuredRichTextDark } from './richtext';
import ChevronCta from './ChevronCta';

import waveDots from '@/public/fabio-affiliate/wave-dots.png';

/**
 * "What Our Traders Say." — Figma 8841:1922 (desktop) / 8811:3007 (mobile).
 * Desktop runs two auto-scrolling rows in opposite directions; mobile is one
 * swipeable row with prev / next and a progress rule.
 */
const ease = [0.25, 0.1, 0.25, 1] as const;

const ACCENTS = [
  { text: '#FFF9A8', bg: 'rgba(255,249,168,0.15)' },
  { text: '#FFDDE8', bg: 'rgba(255,221,232,0.15)' },
  { text: '#DAFFE1', bg: 'rgba(218,255,225,0.15)' },
] as const;

const STAR_OPACITIES = ['opacity-10', 'opacity-40', 'opacity-60', 'opacity-80', 'opacity-100'];

type Testimonial = PagePatrickAffiliateSchema['testimonialsItems'][number];

function Chevron({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden className={className}>
      <path
        d="M16.5 12L9.00002 19.5L7.95002 18.45L14.4 12L7.95002 5.55L9.00002 4.5L16.5 12Z"
        fill="currentColor"
      />
    </svg>
  );
}

function TestimonialCard({ t, index }: { t: Testimonial; index: number }) {
  const accent = ACCENTS[index % ACCENTS.length];
  const [left, ...rest] = (t?.name ?? '').split('—');
  const right = rest.join('—').trim();

  return (
    <article className="flex flex-col justify-between bg-[#112E27] rounded-[0.25rem] w-[45.5rem] h-[37rem] pb-[2rem] lg:w-[50rem] lg:h-[35.875rem]">
      <div className="flex flex-col items-start gap-[2rem] p-[3rem]">
        {t?.avatar ? (
          <GroqImage
            src={t.avatar}
            alt={t.name ?? ''}
            style={{ width: '6rem' }}
            className="object-cover rounded-[0.5rem] !w-[6rem] !h-[6rem]"
          />
        ) : (
          t?.initials && (
            <span
              className="flex justify-center items-center font-geist font-[600] rounded-[0.5rem] w-[6rem] h-[6rem] text-[2.25rem]"
              style={{ backgroundColor: accent.bg, color: accent.text }}
            >
              {t.initials}
            </span>
          )
        )}
        {t?.quote?.length > 0 && (
          <div className="richtext richtext-start prose-p:font-geist prose-p:text-white prose-p:text-[2.25rem] prose-p:leading-[1.5]">
            <PortableText value={t.quote} components={measuredRichTextDark} />
          </div>
        )}
      </div>
      <div className="flex justify-between items-end whitespace-nowrap px-[3rem] pb-[1rem] text-[2.25rem]">
        {t?.stars && (
          <span className="font-geist leading-none" style={{ color: accent.text }}>
            {t.stars}
          </span>
        )}
        {t?.name && (
          <span className="font-geist font-[600] leading-none text-white">
            <span style={{ color: accent.text }}>{left.trim()}</span>
            {right && ` — ${right}`}
          </span>
        )}
      </div>
    </article>
  );
}

function RatingDots() {
  return (
    <span className="flex justify-between items-center w-[7.875rem]">
      {STAR_OPACITIES.map((o) => (
        <span key={o} className={cn('bg-white w-[0.75rem] h-[0.75rem] shrink-0', o)} />
      ))}
    </span>
  );
}

function AutoRow({
  items,
  indices,
  direction,
  fade,
}: {
  items: Testimonial[];
  indices: number[];
  direction: 'forward' | 'backward';
  fade: string;
}) {
  return (
    <div className="relative w-full">
      <div className={cn('absolute inset-y-0 left-0 z-[1] bg-gradient-to-r from-[#001B14] to-transparent pointer-events-none', fade)} />
      <div className={cn('absolute inset-y-0 right-0 z-[1] bg-gradient-to-l from-[#001B14] to-transparent pointer-events-none', fade)} />
      <Carousel
        opts={{ loop: true, dragFree: true, align: 'start' }}
        plugins={[AutoScroll({ speed: 0.8, direction, stopOnInteraction: false, stopOnMouseEnter: true })]}
        className="w-full"
      >
        <CarouselContent className="-ml-[3rem]">
          {[...items, ...items, ...items].map((t, i) => (
            <CarouselItem key={i} className="basis-auto pl-[3rem]">
              <TestimonialCard t={t} index={indices[i % indices.length]} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}

export default function TestimonialsSection({
  data,
}: {
  data: PagePatrickAffiliateSchema;
}) {
  const items = data?.testimonialsItems ?? [];
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;
    const sync = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
    };
    sync();
    api.on('select', sync);
    api.on('reInit', sync);
    return () => {
      api.off('select', sync);
      api.off('reInit', sync);
    };
  }, [api]);

  if (
    !data?.testimonialsChip &&
    !data?.testimonialsHeading?.length &&
    !data?.testimonialsRating &&
    !items.length
  )
    return null;

  const row1 = items.map((_, i) => i).filter((i) => i % 2 === 0);
  const row2 = items.map((_, i) => i).filter((i) => i % 2 !== 0);
  const progress = count > 0 ? ((current + 1) / count) * 100 : 0;

  return (
    <section className="relative overflow-hidden bg-[#001B14] pt-[7rem] pb-[8rem] lg:pt-[15rem] lg:pb-[17.5rem]">
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.09] mix-blend-exclusion pointer-events-none"
        style={{ backgroundImage: `url(${waveDots.src})`, backgroundSize: 'cover' }}
      />

      <div className="flex relative flex-col items-center w-full gap-[5rem]">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center px-[2rem] gap-[3rem]"
        >
          <div className="flex flex-col items-center gap-[1rem] lg:gap-[1rem]">
            {data?.testimonialsChip && (
              <p className="m-0 italic text-center font-blacker text-[2rem] leading-[1.5] text-white/60">
                {data.testimonialsChip}
              </p>
            )}
            {data?.testimonialsHeading?.length > 0 && (
              <div className="richtext richtext-center prose-headings:text-white prose-headings:font-geist prose-headings:font-[500] prose-headings:leading-[1.1] prose-headings:text-[4rem] prose-headings:tracking-[-0.2rem] lg:prose-headings:leading-none lg:prose-headings:text-[8rem] lg:prose-headings:tracking-[-0.4rem] w-[49.75rem] lg:w-[100.25rem] [--hl-deco:none] lg:[--hl-deco:block] lg:[&_h2:last-child]:leading-[1.15] lg:[--hl-dy:0.275rem] [--hl-tracking:0em]">
                <PortableText
                  value={data.testimonialsHeading}
                  components={measuredRichTextDark}
                />
              </div>
            )}
          </div>

          {(data?.testimonialsRating || data?.testimonialsRatingText?.length > 0) && (
            <div className="flex flex-col items-center gap-[1rem]">
              {data?.testimonialsRating && (
                <div className="flex items-center gap-[1rem]">
                  <p className="m-0 font-geist-mono font-[600] tabular-nums text-white/70 text-[2rem] leading-[1.33]">
                    {data.testimonialsRating}
                  </p>
                  <RatingDots />
                </div>
              )}
              {data?.testimonialsRatingText?.length > 0 && (
                <div className="richtext richtext-center w-[43.5rem] prose-p:font-geist prose-p:text-white/70 prose-p:text-[2rem] prose-p:leading-[1.33] prose-strong:font-[600] prose-strong:text-white">
                  <PortableText
                    value={data.testimonialsRatingText}
                    components={measuredRichTextDark}
                  />
                </div>
              )}
            </div>
          )}
        </motion.div>

        {items.length > 0 && (
          <>
            {/* Desktop: two counter-scrolling rows */}
            <div className="hidden lg:flex flex-col w-full gap-[3rem]">
              <AutoRow items={row1.map((i) => items[i])} indices={row1} direction="forward" fade="w-[35rem]" />
              <AutoRow items={row2.map((i) => items[i])} indices={row2} direction="backward" fade="w-[22rem]" />
            </div>

            {/* Mobile: one row, stepped by hand */}
            <div className="flex flex-col items-center w-full lg:hidden gap-[3rem]">
              <Carousel setApi={setApi} opts={{ loop: true, align: 'center' }} className="w-full">
                <CarouselContent className="-ml-[3rem]">
                  {items.map((t, i) => (
                    <CarouselItem key={i} className="basis-auto pl-[3rem]">
                      <TestimonialCard t={t} index={i} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
              <div className="flex items-center gap-[2.5rem] w-[45.5rem]">
                <button
                  type="button"
                  aria-label="Previous"
                  onClick={() => api?.scrollPrev()}
                  className="flex justify-center items-center shrink-0 bg-white/30 text-[#161616] rounded-[1rem] w-[5rem] h-[5rem]"
                >
                  <Chevron className="w-[2.5rem] h-[2.5rem] rotate-180" />
                </button>
                <div className="flex-1 overflow-hidden h-[0.25rem] bg-[#D6EEE7]/15">
                  <div className="h-full bg-[#D6EEE7] transition-[width] duration-500" style={{ width: `${progress}%` }} />
                </div>
                <button
                  type="button"
                  aria-label="Next"
                  onClick={() => api?.scrollNext()}
                  className="flex justify-center items-center shrink-0 bg-white text-[#161616] rounded-[1rem] w-[5rem] h-[5rem]"
                >
                  <Chevron className="w-[2.5rem] h-[2.5rem]" />
                </button>
              </div>
            </div>
          </>
        )}

        {data?.testimonialsCTA && (
          <div className="flex justify-center px-[2rem] w-full lg:w-auto lg:mt-[7.5rem]">
            <ChevronCta
              data={data.testimonialsCTA}
              variant="white"
              language={data?.settings?.language}
            />
          </div>
        )}
      </div>
    </section>
  );
}
